import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  Grid, Typography, Paper, Avatar, Box, Chip, Divider,
  Card, CardContent, CardActionArea, CircularProgress, IconButton,
  useTheme, useMediaQuery
} from "@mui/material";
import { 
  AltRoute, SignalWifiOff, PhoneDisabled, StopCircle, Report, 
  Timer, Room, LocalShipping, MeetingRoom, Refresh, Close
} from "@mui/icons-material";
import Crud_Catalogos from '../../herramientas/Crud_Catalogos';
import ListarMovimientosPanelSupervisor from '../../components/ListarMovimientosPanelSupervisor';
import SiteWrapper from '../../SiteWrapper.react';

// Constantes movidas a un objeto para mejor organización
const CONFIG = {
  ALERTA_CATEGORIA: {
    DESVIO_RUTA: 1298,
    GPS_SIN_SEÑAL: 37,
    UNIDAD_DETENIDA: 1288
  },
  INCIDENCIA: {
    ZONA_INTERMITENCIA_1: 4343,
    ZONA_INTERMITENCIA_2: 304,
    UNIDAD_OPERATIVO: 320
  }
};

const PanelSupervisor = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Estado consolidado para conteos
  const [conteos, setConteos] = useState({
    desvioRuta: 0,
    zonaIntermitencia: 0,
    gpsSinSenal: 0,
    unidadDetenida: 0,
    unidadOperativo: 0,
    temporizadores: 0,
    conGps: 0,
    sinGps: 0,
    unidadProgramada: 0
  });
  
  // Obtener el usuario actual
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("currentUser"))[0].usuario;
    } catch (error) {
      console.error("Error obteniendo usuario actual:", error);
      return { id: null };
    }
  }, []);

  // Función para cargar todos los conteos
  const cargarConteos = async () => {
    if (!currentUser.id) return;
    
    setRefreshing(true);
    try {
      const endpoints = [
        { 
          url: `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.DESVIO_RUTA}`, 
          key: "desvioRuta" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosIncidenciaNavieras/${CONFIG.INCIDENCIA.ZONA_INTERMITENCIA_1}/${CONFIG.INCIDENCIA.ZONA_INTERMITENCIA_2}/${currentUser.id}`, 
          key: "zonaIntermitencia" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.GPS_SIN_SEÑAL}`, 
          key: "gpsSinSenal" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.UNIDAD_DETENIDA}`, 
          key: "unidadDetenida" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosIncidenciaNavieras/${CONFIG.INCIDENCIA.UNIDAD_OPERATIVO}/0/${currentUser.id}`, 
          key: "unidadOperativo" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosTemporizadoresNavieras/${currentUser.id}`, 
          key: "temporizadores" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosGpsNavieras/${currentUser.id}`, 
          key: "conGps" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosSinGpsNavieras/${currentUser.id}`, 
          key: "sinGps" 
        },
        { 
          url: `navieraMovimientos/conteoMovimientosUnidadProgramadaConGpsNavieras/${currentUser.id}`, 
          key: "unidadProgramada" 
        }
      ];
      
      // Realizar todas las solicitudes en paralelo
      await Promise.all(endpoints.map(async (endpoint) => {
        const result = await crud("get", "", "", endpoint.url, "");
        if (result !== null && result !== undefined) {
          setConteos(prev => ({ ...prev, [endpoint.key]: result }));
        }
      }));
    } catch (error) {
      console.error("Error cargando conteos:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Función para actualizar un conteo específico
  const actualizarConteoEspecifico = async (categoria) => {
    //console.log("Actualizando conteo para categoría:", categoria);
    
    if (!currentUser.id) return;
    
    try {
      let endpoint = null;
      let key = null;
      
      // Determinar el endpoint y la clave según la categoría
      switch (categoria) {
        case "Desvío de ruta":
          endpoint = `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.DESVIO_RUTA}`;
          key = "desvioRuta";
          break;
        case "GPS sin señal":
          endpoint = `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.GPS_SIN_SEÑAL}`;
          key = "gpsSinSenal";
          break;
        case "Unidad detenida":
          endpoint = `navieraMovimientos/conteoMovimientosNavieras/${currentUser.id}/${CONFIG.ALERTA_CATEGORIA.UNIDAD_DETENIDA}`;
          key = "unidadDetenida";
          break;
        default:
          //console.log("Categoría no específica, actualizando todos los conteos");
          cargarConteos();
          return;
      }
      
      // Actualizar solo el conteo específico
      if (endpoint && key) {
        //console.log(`Actualizando conteo de ${key} desde ${endpoint}`);
        const result = await crud("get", "", "", endpoint, "");
        if (result !== null && result !== undefined) {
          setConteos(prev => ({ ...prev, [key]: result }));
          //console.log(`Conteo de ${key} actualizado a:`, result);
        }
      }
    } catch (error) {
      console.error("Error actualizando conteo específico:", error);
    }
  };

  // Cargar conteos al montar el componente
  useEffect(() => {
    cargarConteos();
  }, [currentUser.id]);

  // Función simplificada para CRUD
  const crud = (metodo = "get", obj = [], id = "", catalogo = "", stateVar = "") => {    
    return Crud_Catalogos(catalogo, "", metodo, id, obj, "", stateVar, "", [])
      .then(returnVal => returnVal)
      .catch(err => { 
        console.error("Error en operación CRUD:", err); 
        return null;
      });
  };

  // Función que manejará la notificación cuando se atienda una alerta
  const handleAlertaAtendida = (categoriaAlertaAtendida) => {
    //console.log("Panel Supervisor: Alerta atendida en categoría:", categoriaAlertaAtendida);
    actualizarConteoEspecifico(categoriaAlertaAtendida);
  };

  // Definición de paneles
  const paneles = useMemo(() => [
    { 
      title: "Alertas activas",
      items: [
        { 
          icon: <AltRoute fontSize="large" />, 
          count: conteos.desvioRuta, 
          text: "Desvío de ruta",
          color: "#FE6B8B",
          borderColor: "#FF8E53",
          textColor: "white"
        },
        { 
          icon: <SignalWifiOff fontSize="large" />, 
          count: conteos.zonaIntermitencia, 
          text: "GPS en zona de intermitencia",
          color: "#FF8E53",
          borderColor: "#FE6B8B",
          textColor: "white"
        },
        { 
          icon: <PhoneDisabled fontSize="large" />, 
          count: conteos.gpsSinSenal, 
          text: "GPS sin señal",
          color: "#2196F3",
          borderColor: "#21CBF3",
          textColor: "white"
        },
        { 
          icon: <StopCircle fontSize="large" />, 
          count: conteos.unidadDetenida, 
          text: "Unidad detenida",
          color: "#673AB7",
          borderColor: "#9C27B0",
          textColor: "white"
        },
        { 
          icon: <Report fontSize="large" />, 
          count: conteos.unidadOperativo, 
          text: "Solicitud de operativo",
          color: "#F44336",
          borderColor: "#E91E63",
          textColor: "white"
        }
      ]
    },
    {
      title: "Estado de servicios",
      items: [
        { 
          icon: <Timer fontSize="large" />, 
          count: conteos.temporizadores, 
          text: "Temporizador",
          color: "#00796B",
          borderColor: "#009688",
          textColor: "white"
        },
        { 
          icon: <Room fontSize="large" />, 
          count: conteos.conGps, 
          text: "Servicios con GPS",
          color: "#43A047",
          borderColor: "#4CAF50",
          textColor: "white"
        },
        { 
          icon: <MeetingRoom fontSize="large" />, 
          count: conteos.sinGps, 
          text: "Servicios sin GPS",
          color: "#7B1FA2",
          borderColor: "#9C27B0",
          textColor: "white"
        },
        { 
          icon: <LocalShipping fontSize="large" />, 
          count: conteos.unidadProgramada, 
          text: "Unidad programada",
          color: "#5D4037",
          borderColor: "#795548",
          textColor: "white"
        }
      ]
    }
  ], [conteos]);

  const handleClick = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleRefresh = () => {
    cargarConteos();
  };

  // Renderizado condicional para estado de carga
  if (loading && !refreshing) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Cargando panel de supervisor...
        </Typography>
      </Box>
    );
  }

  return (
    <SiteWrapper>
      <Paper 
        elevation={3} 
        sx={{ 
          maxWidth: 1200, 
          margin: "auto", 
          p: { xs: 2, md: 4 }, 
          borderRadius: 2,
          overflow: "hidden"
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            PANEL DE SUPERVISOR
          </Typography>
          <IconButton 
            onClick={handleRefresh} 
            disabled={refreshing}
            color="primary"
            sx={{ p: 1 }}
          >
            <Refresh />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {refreshing && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Actualizando datos...
            </Typography>
          </Box>
        )}
        
        {paneles.map((panel, panelIndex) => (
          <Box key={panelIndex} mb={4}>
            <Typography 
              variant="h5" 
              color="primary" 
              fontWeight="medium" 
              gutterBottom
              sx={{ borderLeft: '4px solid', borderLeftColor: 'primary.main', pl: 2 }}
            >
              {panel.title}
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {panel.items.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: 1,
                      background: `linear-gradient(135deg, ${item.color} 0%, ${item.borderColor} 100%)`,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleClick(item)}
                      sx={{ p: 2, height: '100%' }}
                    >
                      <CardContent>
                        <Box 
                          display="flex" 
                          alignItems="center" 
                          flexDirection={isMobile ? "row" : "column"}
                          textAlign={isMobile ? "left" : "center"}
                        >
                          <Avatar 
                            sx={{ 
                              bgcolor: 'rgba(255, 255, 255, 0.2)', 
                              width: 60, 
                              height: 60,
                              mb: isMobile ? 0 : 2,
                              mr: isMobile ? 2 : 0,
                              color: item.textColor
                            }}
                          >
                            {item.icon}
                          </Avatar>
                          
                          <Box>
                            <Typography 
                              variant="h4" 
                              component="div" 
                              fontWeight="bold"
                              color={item.textColor}
                            >
                              {item.count}
                            </Typography>
                            
                            <Typography 
                              variant="body1" 
                              color={item.textColor}
                              sx={{ 
                                opacity: 0.95,
                                mt: 0.5,
                                fontSize: isMobile ? "0.85rem" : "1rem",
                                fontWeight: "medium"
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth
        maxWidth="xl"
        PaperProps={{
          elevation: 8,
          sx: { 
            borderRadius: 2,
            height: "80vh"
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" component="span">
                Movimientos con{' '}
              </Typography>
              <Chip 
                label={selectedItem ? selectedItem.text : ""}
                color="primary"
                sx={{ fontWeight: "bold" }}
              />
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <ListarMovimientosPanelSupervisor 
            categoria={selectedItem ? selectedItem.text : ""}
            idAlertaCategoriaDesvioRuta={CONFIG.ALERTA_CATEGORIA.DESVIO_RUTA}
            idAlertaCategoriaGpsSinSenal={CONFIG.ALERTA_CATEGORIA.GPS_SIN_SEÑAL}
            idAlertaCategoriaUnidadDetenida={CONFIG.ALERTA_CATEGORIA.UNIDAD_DETENIDA}  
            idIncidencia1={CONFIG.INCIDENCIA.ZONA_INTERMITENCIA_1}
            idIncidencia2={CONFIG.INCIDENCIA.ZONA_INTERMITENCIA_2}
            idIncidencia3={CONFIG.INCIDENCIA.UNIDAD_OPERATIVO}
            idusuario={currentUser.id}
            onActualizarConteos={handleAlertaAtendida} // Pasar callback a componente hijo
          />
        </DialogContent>
      </Dialog>
    </SiteWrapper>
  );
};

export default PanelSupervisor;